/**
 * Preview rendering for the admin email-template page.
 *
 * Every template registered in the backend's EMAIL_TEMPLATES has a builder
 * here that mirrors the branch order of the Python builder it describes, using
 * the sample data below in place of real booking data. The point is that an
 * admin editing a block can see where that paragraph actually lands in the
 * email rather than editing text in the abstract.
 *
 * These are approximations, not the exact bytes the customer receives — the
 * links are rendered as spans so nothing in a preview is clickable, and the
 * shell is a simplified version of email_layout.render_email().
 *
 * Kept out of the page component because it is mostly static strings and was
 * swamping the actual UI code.
 */

export const SAMPLE = {
  name: "Namn Namnsson",
  bookerName: "Namn Namnsson",
  memberName: "Namn Namnsson",
  coachName: "Namn Namnsson",
  coPlayerName: "Namn Namnsson",
  coPlayerId: "1555",
  memberId: "1555",
  username: "1555",
  date: "2026-07-07",
  time: "16:30",
  court: "B4",
  // The grusbana note only fires for B21–B26 in confirm_booking_email.py, so
  // the previewed court has to change with the toggle or the two contradict
  // each other. B27 is deliberately not used: the real code treats it as the
  // hardcourt.
  clayCourt: "B21",
  // Where a moved booking came from, for the Flytt av bokning previews.
  oldCourt: "B6",
  oldTime: "12:30",
  coachPrice: 300,
  coPlayerPrice: 360,
  // On a coach booking the co-player's own line is their court price - 0 kr
  // with a valid playing right for the hour, the usual case - and the lesson
  // surcharge lands on the coach's line instead, 50 kr per co-player
  // (confirm_booking: coach_price += 50 for each, except the ball machine).
  coPlayerPriceWithCoach: 0,
  coachCoPlayerSurcharge: 50,
  guestPrice: 360,
  activityName: "Tennisskola vuxna",
  activityPrice: 250,
  cancelDate: "2026-07-07",
  cancelTime: "15:10",
  // Not a real password. create_admin() ships a hardcoded one; it does not
  // belong in a preview.
  temporaryPassword: "TillfLosen123!",
  // Obviously fake, but the same length as a real token so the fallback URL
  // wraps onto a second line the way it does in the actual email.
  calendarToken: "EXEMPELTOKEN1234567890abcdef",
};

export type CoachMode = "none" | "coach" | "hitting_partner";

export interface IPreviewOptions {
  coach: CoachMode;
  withCoPlayer: boolean;
  withGuest: boolean;
  withClayCourt: boolean;
  dueToRain: boolean;
}

/**
 * When each block appears. Without this the editor shows a column of
 * equal-looking fields, several of which are absent from whatever is being
 * previewed — which is what made the page hard to read.
 *
 * `appliesTo` returns whether the block is in the email for the current
 * options; blocks with no entry are assumed always present.
 */
export const BLOCK_CONTEXT: Record<
  string,
  { hint: string; appliesTo: (o: IPreviewOptions) => boolean }
> = {
  no_show_info: {
    hint: "Visas alltid",
    appliesTo: () => true,
  },
  clay_court_info: {
    hint: "Visas endast för grusbana (B21–B26)",
    appliesTo: (o) => o.withClayCourt,
  },
  coach_lesson_policy: {
    hint: "Visas endast vid bokning med tränare",
    appliesTo: (o) => o.coach === "coach",
  },
  hitting_partner_policy: {
    hint: "Visas endast vid bokning med Hitting Partner",
    appliesTo: (o) => o.coach === "hitting_partner",
  },
  hitting_partner_info: {
    hint: "Visas endast vid bokning med Hitting Partner",
    appliesTo: (o) => o.coach === "hitting_partner",
  },
  rain_indoor_info: {
    hint: "Visas endast vid avbokning pga regn",
    appliesTo: (o) => o.dueToRain,
  },
  reception_contact_info: {
    hint: "Telefonnumret är en tel:-länk i det riktiga utskicket",
    appliesTo: () => true,
  },
  noshow_court_reminder: {
    hint: "Telefonnumret är en tel:-länk i det riktiga utskicket",
    appliesTo: () => true,
  },
  noshow_activity_reminder: {
    hint: "Telefonnumret är en tel:-länk i det riktiga utskicket",
    appliesTo: () => true,
  },
  welcome_heading: {
    hint: "Visas som rubrik istället för ”Hej {namn},”",
    appliesTo: () => true,
  },
};

/**
 * Blocks hidden from the editor while the thing they describe is out of use.
 * The backend still ships the copy and the emails still render it, so this is
 * a single line to undo when the clay courts reopen.
 */
export const HIDDEN_BLOCKS = new Set(["clay_court_info"]);

export const ALL_COACH_MODES: [CoachMode, string][] = [
  ["none", "Vanlig bokning"],
  ["coach", "Med Tränare"],
  ["hitting_partner", "Med Hitting Partner"],
];

/**
 * Which preview controls make sense for a given template. A template with no
 * entry gets no controls — most emails have a single variant.
 *
 * `coachModes` narrows the toggle set: the emails addressed to the coach have
 * no "vanlig bokning" variant, because a booking without a coach never
 * produces them.
 */
export const TEMPLATE_CONTROLS: Record<
  string,
  {
    coach?: boolean;
    coachModes?: [CoachMode, string][];
    participants?: boolean;
    /** Offer the guest toggle. Off for the coach mail: a coach booking can never carry a guest. */
    guests?: boolean;
    clay?: boolean;
    rain?: boolean;
  }
> = {
  booking_confirmation: { coach: true, participants: true, clay: false },
  co_player_booking_confirmation: {},
  booking_cancellation: { coach: true, participants: true, rain: true },
  co_player_cancellation: { rain: true },
  coach_booking_confirmation: {
    coach: true,
    coachModes: ALL_COACH_MODES.slice(1),
    participants: true,
    guests: false,
  },
  coach_booking_cancellation: {
    coach: true,
    coachModes: ALL_COACH_MODES.slice(1),
    rain: true,
  },
  activity_confirmation: {},
  activity_override_cancellation: {},
  unbooked_from_activity: {},
};

/*
 * There was an isWired set here, listing the two templates whose sender read
 * the registry while the other twenty-three saved to DynamoDB and changed
 * nothing that went out. Every template that has editable copy is wired now, so
 * the distinction it drew no longer exists and the set has gone.
 *
 * Ten templates still show no fields at all. That is not the same thing — those
 * emails are entirely dynamic and have no static prose to edit.
 */

// ---------------------------------------------------------------------------
// Shell
// ---------------------------------------------------------------------------

type Signature = "default" | "inline" | "greetings" | "none";
type Footer = "calendar" | "sync" | "login" | "none";

interface IShellOptions {
  /** Raw HTML for the h1. Defaults to the standard "Hej {namn}," line. */
  headingHtml?: string;
  signature?: Signature;
  footer?: Footer;
}

const LINK = "#5856d6";

/** Links are spans: a preview link goes nowhere useful. */
const fakeLink = (text: string) =>
  `<span style="color: #0000ee; text-decoration: underline;">${text}</span>`;

const heading = (text: string) =>
  `<h1 style="font-size: 20px; font-weight: 500; color: #0f0f0f; margin: 0 0 16px 0;">${text}</h1>`;

function signatureHtml(kind: Signature): string {
  switch (kind) {
    case "default":
      return "<p>Vänligen,<br />KLTK</p>";
    case "inline":
      return "<p>Vänligen, KLTK</p>";
    case "greetings":
      return "<p>Hälsningar,<br />KLTK</p>";
    default:
      return "";
  }
}

function footerHtml(kind: Footer): string {
  const webcal = `webcal://kalender.volley.kltk.se/abonnera/${SAMPLE.calendarToken}`;
  const fallback =
    `<p>Om ovan länk inte fungerar - kopiera in nedan länk i din webbläsare:</p>` +
    `<span style="display:block; color: ${LINK}; word-break: break-all;">${webcal}</span>`;

  switch (kind) {
    case "calendar":
      return `${fakeLink("Lägg till enskild bokning i kalender")}<br /><br />${fakeLink(
        "Synkronisera alla bokningar med kalendern"
      )}${fallback}`;
    case "sync":
      return `${fakeLink("Synkronisera alla bokningar med kalendern")}${fallback}`;
    case "login":
      return "";
    default:
      return "";
  }
}

/** The "Logga in" button plus the footer link paragraph that goes with it. */
const LOGIN_BUTTON = `
  <div style="margin-top: 20px;">
    <span style="display: inline-block; padding: 14px 22px; border-radius: 50px; font-size: 16px; background-color: ${LINK}; color: #fff;">Logga in</span>
  </div>`;

const LOGIN_FOOTER_NOTE = `
  <p style="margin-top: 20px; font-size: 14px;">Om du har problem med länken ovan, vänligen kopiera denna länk och klistra in den i din webbläsare - <span style="color: ${LINK};">Länk</span></p>`;

/**
 * Where an admin's own paragraphs go: after the email's copy, before the
 * signature — the same slot render_email(custom_html=...) fills. The builders
 * never see them; buildPreview swaps the marker for the rendered blocks.
 */
const CUSTOM_MARKER = "<!--custom-blocks-->";

function shell(bodyHtml: string, options: IShellOptions = {}): string {
  const {
    headingHtml = heading(`Hej ${SAMPLE.name},`),
    signature = "default",
    footer = "none",
  } = options;

  return `
  <!DOCTYPE html>
  <html lang="sv">
    <head>
      <meta charset="UTF-8" />
      <style>
        @font-face { font-family: "Switzer"; src: url("https://cdn.techmdw.com/nuioah8w9ey8qj2134/files/Switzer-Variable.ttf"); }
        body { margin: 0; }
        p { margin: 12px 0; }
      </style>
    </head>
    <body style="font-family: 'Switzer', sans-serif; margin: 0; font-size: 17px; color: #444; background-color: #fff;">
      <div style="width: 100%; display: flex; justify-content: center; background-color: #ffffff;">
        <div style="width: 100%; max-width: 640px; background-color: #f2f2f7;">
          <div style="display: flex; align-items: center; justify-content: center; padding: 20px; background-color: #272d7c;">
            <img src="https://cdn.techmdw.com/nuioah8w9ey8qj2134/files/Kltk%20Logo.png" style="width: 100px;" width="100" />
          </div>
          <div style="padding: 50px 30px;">
            ${headingHtml}
            <div style="font-size: 18px; margin-bottom: 15px;">
              ${bodyHtml}
              ${CUSTOM_MARKER}
              ${signatureHtml(signature)}
              ${footerHtml(footer)}
              ${footer === "login" ? LOGIN_BUTTON : ""}
            </div>
          </div>
          <div style="padding: 60px 30px 50px 30px;">
            <div style="width: 100%; height: 1px; background-color: ${LINK};"></div>
            ${footer === "login" ? LOGIN_FOOTER_NOTE : ""}
          </div>
        </div>
      </div>
    </body>
  </html>
  `;
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

/**
 * Wrap an editable block so it is visibly distinct from the fixed copy around
 * it. Without this the preview is a wall of text and there is no way to tell
 * which paragraphs the fields on the left control.
 */
function editable(text: string, key: string, focusedKey?: string): string {
  const focused = key === focusedKey;
  return `<p style="
    margin: 12px 0;
    padding: 8px 12px;
    border-left: 3px solid ${focused ? "#5856d6" : "#c9c8e8"};
    background-color: ${focused ? "#e9e8fb" : "#f7f7fc"};
    ${focused ? "box-shadow: 0 0 0 1px #5856d6;" : ""}
  ">${text}</p>`;
}

/** Same treatment, but for a block that is rendered as the h1. */
function editableHeading(
  text: string,
  key: string,
  focusedKey?: string
): string {
  const focused = key === focusedKey;
  return `<h1 style="
    font-size: 20px;
    font-weight: 500;
    color: #0f0f0f;
    margin: 0 0 16px 0;
    padding: 8px 12px;
    border-left: 3px solid ${focused ? "#5856d6" : "#c9c8e8"};
    background-color: ${focused ? "#e9e8fb" : "#f7f7fc"};
    ${focused ? "box-shadow: 0 0 0 1px #5856d6;" : ""}
  ">${text}</h1>`;
}

const bold = (text: string | number) =>
  `<span style="font-weight: 600">${text}</span>`;

type Builder = (
  values: Record<string, string>,
  options: IPreviewOptions,
  focusedKey?: string
) => string;

/** Court shown for the current grusbana toggle. */
const courtFor = (o: IPreviewOptions) =>
  o.withClayCourt ? SAMPLE.clayCourt : SAMPLE.court;

const rainSuffix = (o: IPreviewOptions) => (o.dueToRain ? " pga regn" : "");

/** Mirrors the " Tränare X, " / " Hitting Partner X, " fragment. */
function coachFragment(o: IPreviewOptions): string {
  if (o.coach === "hitting_partner")
    return ` Hitting Partner ${SAMPLE.coachName}, `;
  if (o.coach === "coach") return ` Tränare ${SAMPLE.coachName}, `;
  return "";
}

// ---------------------------------------------------------------------------
// Per-email builders
// ---------------------------------------------------------------------------

/**
 * Mirrors the branch order in confirm_booking_email.py: clay note, coach
 * policy, per-participant price lines from final_price, total, no-show.
 */
const bookingConfirmation: Builder = (values, options, focusedKey) => {
  const { coach, withCoPlayer, withGuest, withClayCourt } = options;
  const court = courtFor(options);
  let html = `<p>Du har bokat en tid ${bold(SAMPLE.date)} klockan ${bold(
    SAMPLE.time
  )} på Bana ${bold(court + ".")}</p>`;

  if (withClayCourt)
    html += editable(values.clay_court_info ?? "", "clay_court_info", focusedKey);

  if (coach === "hitting_partner") {
    html += editable(
      values.hitting_partner_policy ?? "",
      "hitting_partner_policy",
      focusedKey
    );
    html += editable(
      values.hitting_partner_info ?? "",
      "hitting_partner_info",
      focusedKey
    );
  } else if (coach === "coach") {
    html += editable(
      values.coach_lesson_policy ?? "",
      "coach_lesson_policy",
      focusedKey
    );
  }

  let total = 0;
  if (withGuest) {
    total += SAMPLE.guestPrice;
    html += `<p>Gäst: ${SAMPLE.guestPrice} kr.</p>`;
  }
  if (withCoPlayer) {
    const coPlayerPrice =
      coach === "coach" ? SAMPLE.coPlayerPriceWithCoach : SAMPLE.coPlayerPrice;
    total += coPlayerPrice;
    html += `<p>Medspelare ${SAMPLE.coPlayerName} (${SAMPLE.coPlayerId}): ${coPlayerPrice} kr.</p>`;
  }
  if (coach !== "none") {
    const coachPrice =
      coach === "coach" && withCoPlayer
        ? SAMPLE.coachPrice + SAMPLE.coachCoPlayerSurcharge
        : SAMPLE.coachPrice;
    total += coachPrice;
    const label =
      coach === "hitting_partner"
        ? `Hitting Partner ${SAMPLE.coachName}:`
        : `Tränare ${SAMPLE.coachName}:`;
    html += `<p>${label} ${coachPrice} kr.</p>`;
  }
  html += `<p>Totalt pris: ${total} kr.</p>`;
  html += editable(values.no_show_info ?? "", "no_show_info", focusedKey);
  return shell(html, { footer: "calendar" });
};

/** Mirrors co_player_booking.py — no prices, no coach. */
const coPlayerConfirmation: Builder = (values, options, focusedKey) => {
  let html = `<p>Du har lagts till som medspelare av ${bold(
    `${SAMPLE.bookerName} ${SAMPLE.date}`
  )} klockan ${bold(SAMPLE.time)} på Bana ${bold(courtFor(options))}.</p>`;
  if (options.withClayCourt)
    html += editable(values.clay_court_info ?? "", "clay_court_info", focusedKey);
  html += editable(values.no_show_info ?? "", "no_show_info", focusedKey);
  return shell(html, { footer: "calendar" });
};

/** Mirrors cancel_booking_email.py, including the rain branch. */
const bookingCancellation: Builder = (values, options, focusedKey) => {
  const parties = [
    coachFragment(options),
    options.withCoPlayer ? `${SAMPLE.coPlayerName}, ` : "",
    options.withGuest ? "en gäst" : "",
  ].join("");
  const typeOfBooking = options.coach === "none" ? "bokning" : "tennisträning";

  let html = `<p>Din ${bold(
    `${typeOfBooking} ${SAMPLE.date}`
  )} klockan ${bold(SAMPLE.time)} på Bana ${bold(
    courtFor(options)
  )} med ${bold(parties)} har blivit avbokad${rainSuffix(options)}.</p>`;

  if (options.dueToRain)
    html += editable(values.rain_indoor_info ?? "", "rain_indoor_info", focusedKey);

  html += editable(values.no_show_info ?? "", "no_show_info", focusedKey);
  return shell(html);
};

const coPlayerCancellation: Builder = (_values, options) =>
  shell(
    `<p>Din bokning som medspelare till ${SAMPLE.bookerName} på KLTK ${bold(
      SAMPLE.date
    )} klockan ${bold(SAMPLE.time)} på Bana ${bold(SAMPLE.court)} har avbokats${rainSuffix(
      options
    )}.</p>`
  );

/** Mirrors coach_confirmed_email.py — goes to the coach, not the member. */
const coachBookingConfirmation: Builder = (_values, options) => {
  const isHp = options.coach === "hitting_partner";
  let html = isHp
    ? `<p>${SAMPLE.memberName} har bokat en tid med dig som Hitting Partner på KLTK ${bold(SAMPLE.date)} klockan ${bold(SAMPLE.time)} på Bana ${bold(SAMPLE.court)}.</p>`
    : `<p>${SAMPLE.memberName} har bokat en lektion med dig på KLTK ${bold(SAMPLE.date)} klockan ${bold(SAMPLE.time)} på Bana ${bold(SAMPLE.court)}.</p>`;

  // No guest line: the booking flow never lets a guest onto a coach booking,
  // and a hitting partner plays with the booker alone, so no co-player either.
  let total = 0;
  const withCoPlayer = options.withCoPlayer && !isHp;
  if (withCoPlayer) {
    total += SAMPLE.coPlayerPriceWithCoach;
    html += `<p>Pris för ${SAMPLE.coPlayerName} (${SAMPLE.coPlayerId}): ${SAMPLE.coPlayerPriceWithCoach} kr.</p>`;
  }
  const coachPrice = withCoPlayer
    ? SAMPLE.coachPrice + SAMPLE.coachCoPlayerSurcharge
    : SAMPLE.coachPrice;
  total += coachPrice;
  html += `<p>${
    isHp
      ? `Pris Hitting Partner ${SAMPLE.coachName}:`
      : `Pris tränare ${SAMPLE.coachName}:`
  } ${coachPrice} kr.</p>`;
  html += `<p>Totalt pris: ${total} kr</p>`;
  return shell(html, { footer: "sync" });
};

const coachBookingCancellation: Builder = (_values, options) => {
  const word = options.coach === "hitting_partner" ? "ert pass" : "er lektion";
  return shell(
    `<p>${SAMPLE.memberName} har avbokat ${word} på KLTK ${bold(SAMPLE.date)} klockan ${bold(
      SAMPLE.time
    )} på Bana ${bold(SAMPLE.court)}${rainSuffix(options)}.</p>`
  );
};

const removedCoPlayer: Builder = () =>
  shell(
    `<p>Du har blivit borttagen som medspelare på en bokning den ${bold(
      SAMPLE.date
    )} klockan ${bold(SAMPLE.time)} på ${bold(`Bana ${SAMPLE.court}`)}</p>`
  );

const bookingUpdated: Builder = (values, _options, focusedKey) => {
  // email_events is built by the caller; these are representative entries.
  const events = [
    `Tränare ${SAMPLE.coachName} har lagts till.`,
    `Medspelare ${SAMPLE.coPlayerName} (${SAMPLE.coPlayerId}) har lagts till.`,
  ];
  const html =
    editable(values["booking_change_intro"] ?? "", "booking_change_intro", focusedKey) +
    `<p>${bold(SAMPLE.date)} klockan ${bold(SAMPLE.time)} på Bana ${bold(
      SAMPLE.court
    )}</p>` +
    events.map((e) => `<p>${e}</p>`).join("");
  return shell(html);
};

/**
 * Second paragraph of a move mail, mirroring move_summary() in
 * move_booking_email.py: only what actually changed is named. The preview
 * shows the both-changed case, which is the longest of the three.
 */
const moveSummary = (o: IPreviewOptions) =>
  `Flytt av bana från ${SAMPLE.oldCourt} till ${courtFor(o)}, ` +
  `och av tid från klockan ${SAMPLE.oldTime} till ${SAMPLE.time}.`;

/** The opening line differs per recipient; everything below it does not. */
const bookingMoved =
  (blockKey: string | null, coachIntro?: string): Builder =>
  (values, options, focusedKey) =>
    shell(
      (blockKey
        ? editable(values[blockKey] ?? "", blockKey, focusedKey)
        : `<p>${coachIntro}</p>`) +
        `<p>${bold(SAMPLE.date)} klockan ${bold(SAMPLE.time)} på Bana ${bold(
          courtFor(options)
        )}</p>` +
        `<p>${moveSummary(options)}</p>`
    );

/** The four no-show notices differ only in which blocks they list. */
const noShow =
  (keys: string[]): Builder =>
  (values, _options, focusedKey) =>
    shell(
      keys.map((k) => editable(values[k] ?? "", k, focusedKey)).join(""),
      { signature: "none", footer: "login" }
    );

/**
 * No show #3 has no HTML shell at all — noshow_emails.py sends a bare string
 * to kansli@kltk.se. Shown as-is so it is obvious this one is unstyled.
 */
const noShowOfficeNotice: Builder = () => `
  <!DOCTYPE html>
  <html lang="sv"><body style="margin:0; padding:24px; font-family: ui-monospace, monospace; font-size: 14px; color: #444; background: #f7f7fc;">
    <p style="margin:0 0 12px 0; color:#8a8a8f;">Detta utskick saknar HTML-mall — det skickas som ren text till kansliet.</p>
    <div style="padding:12px; background:#fff; border:1px solid #e5e5ea;">Medlem ${SAMPLE.memberName} har fått sin tredje noshow.</div>
  </body></html>`;

const activityConfirmation: Builder = (values, _options, focusedKey) => {
  let html = `<p>Du har bokat aktiviteten ${bold(SAMPLE.activityName)} den ${bold(
    SAMPLE.date
  )} klockan ${bold(SAMPLE.time)} på Bana ${bold(SAMPLE.court)}.</p>`;
  html += `<p>Pris: ${SAMPLE.activityPrice} kr</p>`;
  html += editable(
    values.activity_cancellation_policy ?? "",
    "activity_cancellation_policy",
    focusedKey
  );
  return shell(html, { signature: "inline", footer: "calendar" });
};

const activityOverrideCancellation: Builder = (values, _options, focusedKey) => {
  let html = `<p>Vi har tyvärr behövt avboka din tid ${bold(
    SAMPLE.date
  )} kl. ${bold(SAMPLE.time)} på Bana ${bold(
    SAMPLE.court
  )} på grund av en aktivitet.</p>`;
  html += editable(
    values.activity_override_apology ?? "",
    "activity_override_apology",
    focusedKey
  );
  html += editable(
    values.reception_contact_info ?? "",
    "reception_contact_info",
    focusedKey
  );
  return shell(html, { signature: "none", footer: "login" });
};

const activitySlotCancelled: Builder = () =>
  shell(
    `<p>Vi har tyvärr behövt avboka aktiviteten ${bold(
      SAMPLE.activityName
    )} den ${bold(SAMPLE.date)} klockan ${bold(SAMPLE.time)}.</p>`,
    { signature: "inline" }
  );

const unbookedFromActivity: Builder = (values, _options, focusedKey) => {
  let html = `<p>Din bokning av aktiviteten ${bold(
    SAMPLE.activityName
  )}, den ${bold(SAMPLE.date)} klockan ${bold(SAMPLE.time)} på Bana ${bold(
    SAMPLE.court
  )} har avbokats.</p>`;
  html += editable(
    values.activity_cancellation_policy ?? "",
    "activity_cancellation_policy",
    focusedKey
  );
  html += editable(
    values.reception_contact_info ?? "",
    "reception_contact_info",
    focusedKey
  );
  return shell(html, { signature: "none", footer: "login" });
};

/** Addressed to the office, so no "Hej {namn}," and no signature. */
const lateActivityCancellationOffice: Builder = () =>
  shell(
    `<p>Medlemmen ${bold(SAMPLE.memberName)} med medlemsnr ${SAMPLE.memberId} har avbokat aktiviteten ${bold(SAMPLE.activityName)} den ${bold(SAMPLE.date)} kl ${bold(SAMPLE.time)} för sent. Medlemmen avbokade aktiviteten den ${SAMPLE.cancelDate} kl ${SAMPLE.cancelTime}.</p>`,
    { headingHtml: heading("Hej Kansliet"), signature: "none" }
  );

/**
 * The three welcome mails differ only in which lambda creates the account —
 * except that the member one keeps its own longer wording, so which key
 * carries that sentence depends on the template being previewed. Reading it
 * off values rather than the template name means the preview cannot drift from
 * whatever blocks the backend actually declared.
 */
const temporaryPassword: Builder = (values, _options, focusedKey) => {
  const infoKey =
    "member_temporary_password_info" in values
      ? "member_temporary_password_info"
      : "temporary_password_info";
  const html =
    `<p>Ditt användarnamn är ${SAMPLE.username} och ditt temporära lösenord är</p>` +
    `<p style="font-weight:600">${SAMPLE.temporaryPassword}</p>` +
    editable(values[infoKey] ?? "", infoKey, focusedKey);
  return shell(html, {
    headingHtml: editableHeading(
      values.welcome_heading ?? "",
      "welcome_heading",
      focusedKey
    ),
    signature: "none",
    footer: "login",
  });
};

/** Sent by the Cognito custom email sender lambda when a member asks for a reset code. */
const passwordReset: Builder = (values, _options, focusedKey) =>
  shell(
    `<p>Här är koden för att återställa ditt lösenord:</p>` +
      `<p style="font-weight:600; font-size: 24px; letter-spacing: 4px;">123456</p>` +
      editable(
        values.password_reset_info ?? "",
        "password_reset_info",
        focusedKey
      )
  );

const rehabConfirmation: Builder = () =>
  shell(
    `<p>Du har bokat ${bold(`Rehab-badet ${SAMPLE.date}`)} klockan ${bold(
      SAMPLE.time
    )}.</p>`
  );

const rehabCancellation: Builder = () =>
  shell(
    `<p>Din ${bold(`bokning ${SAMPLE.date}`)} klockan ${bold(
      SAMPLE.time
    )} av ${bold("Rehab-badet")} har blivit avbokad.</p>`
  );

const queueActivityAvailable: Builder = (values, _options, focusedKey) => {
  const html =
    `<p>Nu finns det lediga platser på aktiviteten ${bold(
      SAMPLE.activityName
    )} på KLTK den ${bold(`${SAMPLE.date} kl. ${SAMPLE.time}.`)}</p>` +
    editable(
      values.queue_booking_prompt ?? "",
      "queue_booking_prompt",
      focusedKey
    );
  return shell(html, { signature: "greetings" });
};

const queueCourtAvailable: Builder = (values, _options, focusedKey) => {
  const html =
    `<p>Nu finns det lediga banor på KLTK den ${bold(
      `${SAMPLE.date} kl. ${SAMPLE.time}.`
    )}</p>` +
    editable(
      values.queue_booking_prompt ?? "",
      "queue_booking_prompt",
      focusedKey
    );
  return shell(html, { signature: "greetings" });
};

const PREVIEW_BUILDERS: Record<string, Builder> = {
  booking_confirmation: bookingConfirmation,
  co_player_booking_confirmation: coPlayerConfirmation,
  booking_cancellation: bookingCancellation,
  co_player_cancellation: coPlayerCancellation,
  coach_booking_confirmation: coachBookingConfirmation,
  coach_booking_cancellation: coachBookingCancellation,
  removed_co_player: removedCoPlayer,
  booking_updated: bookingUpdated,
  booking_moved: bookingMoved("booking_change_intro"),
  booking_moved_co_player: bookingMoved("booking_change_intro"),
  booking_moved_coach: bookingMoved(
    null,
    "En lektion du är bokad på har flyttats:"
  ),
  noshow_court_first: noShow([
    "noshow_court_intro",
    "noshow_court_reminder",
    "noshow_court_rules",
    "noshow_court_first_warning",
  ]),
  noshow_court_second: noShow([
    "noshow_court_intro",
    "noshow_court_reminder",
    "noshow_court_rules",
    "noshow_court_second_warning",
  ]),
  noshow_activity_first: noShow([
    "noshow_activity_intro",
    "noshow_activity_reminder",
    "noshow_activity_rules",
    "noshow_activity_first_warning",
  ]),
  noshow_activity_second: noShow([
    "noshow_activity_intro",
    "noshow_activity_reminder",
    "noshow_activity_rules",
    "noshow_activity_second_warning",
  ]),
  noshow_office_notice: noShowOfficeNotice,
  activity_confirmation: activityConfirmation,
  activity_override_cancellation: activityOverrideCancellation,
  activity_slot_cancelled: activitySlotCancelled,
  unbooked_from_activity: unbookedFromActivity,
  late_activity_cancellation_office: lateActivityCancellationOffice,
  admin_temporary_password: temporaryPassword,
  coach_temporary_password: temporaryPassword,
  member_temporary_password: temporaryPassword,
  member_password_reset: passwordReset,
  rehab_confirmation: rehabConfirmation,
  rehab_cancellation: rehabCancellation,
  queue_activity_available: queueActivityAvailable,
  queue_court_available: queueCourtAvailable,
};

const escapeHtml = (text: string) =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * A custom paragraph as the real email renders it: escaped, line breaks kept.
 * An empty one still takes its place in the preview so the admin can see
 * where the text will land, even though the sent email skips it.
 */
function customBlock(text: string, key: string, focusedKey?: string): string {
  const trimmed = text.trim();
  const body = trimmed
    ? escapeHtml(trimmed).replace(/\n/g, "<br />")
    : `<span style="color: #8a8a8f; font-style: italic;">Tomt stycke – skriv texten till vänster</span>`;
  return editable(body, key, focusedKey);
}

export function buildPreview(
  templateName: string | undefined,
  values: Record<string, string>,
  options: IPreviewOptions,
  focusedKey?: string,
  customKeys: string[] = []
): string {
  const build = templateName ? PREVIEW_BUILDERS[templateName] : undefined;
  if (!build) return shell("<p>Ingen förhandsgranskning för denna mall.</p>");
  const custom = customKeys
    .map((key) => customBlock(values[key] ?? "", key, focusedKey))
    .join("");
  return build(values, options, focusedKey).replace(CUSTOM_MARKER, custom);
}
